import type { ReactNode } from "react";
import { useGestaltung } from "./gestaltung";
import { Kopfnavigation } from "./Kopfnavigation";
import { Scrollbereich } from "./Scrollbereich";
import { useEingabesperre, useSperre } from "./sperre";
import { Symbol } from "./Symbol";

/**
 * Neutrale Ansicht ohne Jagdzustand (oberflaeche.md §1.2, §3.1; design/README §9.12, Referenz `phone--neutral`,
 * `#s-einstellungen`, `#s-drueckjagden`): Kopfnavigation (eine Reihe mit „mehr“, K101 (2)), dann genau ein `main` mit dem
 * Ansichtsnamen: Titel, Inhalt im Scrollbereich mit Hinweisstreifen, Handlungen fest unten (`unten`). Quer (`k101-quer`, K101
 * (1)) stehen Kopfnavigation, Titel und Handlungen links, die Liste rechts in voller Höhe. Während der
 * Eingabesperre ist `main` nicht bedienbar und trägt `aria-busy` (§5, V-16).
 *
 * `art`: `liste` (Vorgabe) mit Scrollbereich; `kanal`: Verlauf und Eingabe füllen `main` (Kanal außerhalb einer
 * Drückjagd); `frei`: der Inhalt ordnet sich selbst (Fläche, eigener Scrollbereich).
 */
export function Ansicht({ name, titel, unterzeile, ohneKopf, unten, art = "liste", children }: {
  name: string; titel?: string; symbol?: string; unterzeile?: ReactNode; ohneKopf?: boolean; unten?: ReactNode;
  art?: "liste" | "kanal" | "frei"; children: ReactNode;
}) {
  const gesperrt = useSperre("ablauf", name);
  const beschaeftigt = useEingabesperre(gesperrt);
  const wurzel = useGestaltung();
  const kopf = (
    <div className="neutral__kopf">
      <h1 className="neutral__t">{titel ?? name}</h1>
      {unterzeile ? <span className="neutral__unter">{unterzeile}</span> : null}
    </div>
  );
  const quer = !ohneKopf && art !== "frei" ? " k101-quer" : "";
  return (
    <div ref={wurzel} className={`ansicht ansicht--neutral phone--neutral${gesperrt ? " ist-gesperrt" : ""}`}>
      {ohneKopf ? null : <Kopfnavigation />}
      <main className={`feld ${art === "kanal" ? "feld--kanal feld--neutral-kanal" : "feld--neutral"}${quer}`} aria-label={name} inert={gesperrt} aria-busy={beschaeftigt || undefined}>
        {kopf}
        {art === "liste" ? <Scrollbereich>{children}</Scrollbereich> : children}
        {unten ? <div className="neutral__unten">{unten}</div> : null}
      </main>
    </div>
  );
}

/** Wortmarke der Eingangsansichten (Anmeldung, Einladung, Registrierung, Wiederherstellung). */
export function Wortmarke({ unterzeile }: { unterzeile: string }) {
  return (
    <div className="wortmarke">
      <span className="wortmarke__zeichen"><Symbol name="stand-erreicht" /></span>
      <div><b>Drückjagd</b><span>{unterzeile}</span></div>
    </div>
  );
}

/**
 * Eingangsansicht ohne Kopfnavigation (oberflaeche.md §3.1: Anmeldung, Einladung, Registrierung, Wiederherstellung;
 * Referenz `#s-einladung`): Wortmarke und Titel (`heading` Ebene 1), Text und Felder im Scrollbereich, die Handlungen fest
 * unten (`drei`: eine große und zwei kleine Kacheln wie in der Einladung).
 */
export function Eingang({ name, titel, unter, unten, drei, schritt, children }: {
  name: string; titel: string; unter: string; unten?: ReactNode; drei?: boolean;
  /** Schritt der Ansicht (z. B. Code anfordern → bestätigen): ein neuer Schritt sperrt die Eingabe wie ein Ansichtswechsel */
  schritt?: string; children: ReactNode;
}) {
  const gesperrt = useSperre("ablauf", `${name}|${schritt ?? ""}`);
  const beschaeftigt = useEingabesperre(gesperrt);
  const wurzel = useGestaltung();
  return (
    <div ref={wurzel} className={`ansicht ansicht--neutral phone--neutral${gesperrt ? " ist-gesperrt" : ""}`}>
      <main className="feld feld--neutral" aria-label={name} inert={gesperrt} aria-busy={beschaeftigt || undefined}>
        <div className="neutral__kopf">
          <Wortmarke unterzeile={unter} />
          <h1 className="neutral__t">{titel}</h1>
        </div>
        <Scrollbereich>{children}</Scrollbereich>
        {unten ? <div className={`neutral__unten${drei ? " neutral__unten--3" : ""}`}>{unten}</div> : null}
      </main>
    </div>
  );
}
